import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Stack,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const formatDate = (dateString) => {
    if (!dateString || !dateString.includes('-')) return dateString;
    const [y, m, d] = dateString.split('-');
    return `${d}-${m}-${y}`;
};

function VisitorTable({ records, onAddNew, onEdit, onDelete }) {
    /* =======================
       DELETE DIALOG STATE
       ======================= */
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);

    const handleDeleteClick = (record) => {
        setRecordToDelete(record);
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = () => {
        if (recordToDelete) {
            onDelete(recordToDelete.id);
        }
        setOpenDeleteDialog(false);
        setRecordToDelete(null);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setRecordToDelete(null);
    };

    /* =======================
       FILTER STATE
       ======================= */
    const [filters, setFilters] = useState({
        id: '',
        name: '',
        dateFrom: '',
        dateTo: '',
        mobile: '',
        aadhar: '',
        epic_no: '',
        village: '',
        ganName: '',
        zpName: '',
        status: '',
    });

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((f) => ({ ...f, [name]: value }));
    };

    /* =======================
       PERFORMANCE SAFE FILTER
       ======================= */
    const filteredRecords = useMemo(() => {
        return records.filter((r) => {
            const rDate = r.v_entry_date || '';

            const dateMatch =
                (!filters.dateFrom || rDate >= filters.dateFrom) &&
                (!filters.dateTo || rDate <= filters.dateTo);

            return (
                (r.id?.toString() || '').includes(filters.id) &&
                (r.name || '').toLowerCase().includes(filters.name.toLowerCase()) &&
                dateMatch &&
                (r.mobile || '').includes(filters.mobile) &&
                (r.aadhar || '').includes(filters.aadhar) &&
                (r.epic_no || '').includes(filters.epic_no) &&
                (r.karad?.village || r.other?.village || '')
                    .toLowerCase()
                    .includes(filters.village.toLowerCase()) &&
                (r.karad?.ganName || '')
                    .toLowerCase()
                    .includes(filters.ganName.toLowerCase()) &&
                (r.karad?.zpName || '')
                    .toLowerCase()
                    .includes(filters.zpName.toLowerCase()) &&
                (r.status || '')
                    .toLowerCase()
                    .includes(filters.status.toLowerCase())
            );
        });
    }, [records, filters]);

    /* =======================
       EXCEL EXPORT
       ======================= */
    const handleDownloadExcel = () => {
        const data = filteredRecords.map((r) => ({
            ID: r.id,
            Name: r.name,
            Date: r.v_entry_date,
            Mobile: r.mobile,
            Aadhar: r.aadhar,
            ConsumerNo: r.epic_no,
            Vidhansabha: r.vidhansabha,
            BoothNo: r.booth_no,
            Village: r.karad?.village || r.other?.village,
            Taluka: r.other?.taluka,
            District: r.other?.district,
            GanName: r.karad?.ganName,
            ZPName: r.karad?.zpName,
            Reference: r.karad?.reference,
            Purpose: r.purpose,
            Status: r.status,
            Remarks: r.remarks,
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Visitors');
        XLSX.writeFile(wb, 'Visitors.xlsx');
    };

    return (
        <Paper sx={{ p: 2 }} elevation={4}>
            {/* HEADER */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0b4a6f' }}>
                    Solar Records
                </Typography>

                <Chip
                    label={`${filteredRecords.length} records`}
                    sx={{ ml: 2 }}
                    color="primary"
                    variant="outlined"
                    size="small"
                />

                <Box sx={{ flexGrow: 1 }} />

                <Button
                    variant="outlined"
                    color="success"
                    onClick={handleDownloadExcel}
                    sx={{ mr: 1 }}
                >
                    Download Excel
                </Button>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={onAddNew}
                >
                    Add New
                </Button>
            </Box>

            {/* TABLE */}
            <TableContainer
                sx={{
                    maxHeight: 'calc(100vh - 220px)',
                    border: '1px solid #ddd',
                    borderRadius: 1,
                }}
            >
                <Table stickyHeader size="small">
                    {/* TABLE HEAD */}
                    <TableHead>
                        <TableRow>
                            {[
                                'ID',
                                'Name',
                                'Date',
                                'Mobile',
                                'Aadhar',
                                'Consumer No',
                                'Village',
                                'Gan',
                                'ZP',
                                'Status',
                                'Actions',
                            ].map((h) => (
                                <TableCell
                                    key={h}
                                    sx={{ bgcolor: '#0b4a6f', color: 'white', fontWeight: 'bold' }}
                                >
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>

                        {/* FILTER ROW */}
                        <TableRow>
                            {/* ID */}
                            <TableCell sx={{ bgcolor: '#e1f5fe' }}>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    name="id"
                                    value={filters.id}
                                    onChange={handleFilterChange}
                                    placeholder="ID"
                                    InputProps={{ disableUnderline: true }}
                                    sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999' }}
                                />
                            </TableCell>

                            {/* NAME */}
                            <TableCell sx={{ bgcolor: '#e1f5fe' }}>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    name="name"
                                    value={filters.name}
                                    onChange={handleFilterChange}
                                    placeholder="Name"
                                    InputProps={{
                                        disableUnderline: true,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999' }}
                                />
                            </TableCell>

                            {/* DATE RANGE */}
                            <TableCell sx={{ bgcolor: '#e1f5fe' }}>
                                <Stack spacing={0.5}>
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        type="date"
                                        name="dateFrom"
                                        value={filters.dateFrom}
                                        onChange={handleFilterChange}
                                        InputProps={{ disableUnderline: true }}
                                        sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999' }}
                                    />
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        type="date"
                                        name="dateTo"
                                        value={filters.dateTo}
                                        onChange={handleFilterChange}
                                        InputProps={{ disableUnderline: true }}
                                        sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999' }}
                                    />
                                </Stack>
                            </TableCell>

                            {/* MOBILE */}
                            <TableCell sx={{ bgcolor: '#e1f5fe' }}>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    name="mobile"
                                    value={filters.mobile}
                                    onChange={handleFilterChange}
                                    placeholder="Mobile"
                                    InputProps={{ disableUnderline: true }}
                                    sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999' }}
                                />
                            </TableCell>

                            {/* AADHAR */}
                            <TableCell sx={{ bgcolor: '#e1f5fe' }}>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    name="aadhar"
                                    value={filters.aadhar}
                                    onChange={handleFilterChange}
                                    placeholder="Aadhar"
                                    InputProps={{ disableUnderline: true }}
                                    sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999' }}
                                />
                            </TableCell>

                            {/* CONSUMER */}
                            <TableCell sx={{ bgcolor: '#e1f5fe' }}>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    name="epic_no"
                                    value={filters.epic_no}
                                    onChange={handleFilterChange}
                                    placeholder="Consumer"
                                    InputProps={{ disableUnderline: true }}
                                    sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999' }}
                                />
                            </TableCell>

                            {/* VILLAGE */}
                            <TableCell sx={{ bgcolor: '#e1f5fe' }}>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    name="village"
                                    value={filters.village}
                                    onChange={handleFilterChange}
                                    placeholder="Village"
                                    InputProps={{ disableUnderline: true }}
                                    sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999' }}
                                />
                            </TableCell>

                            {/* GAN */}
                            <TableCell sx={{ bgcolor: '#e1f5fe' }}>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    name="ganName"
                                    value={filters.ganName}
                                    onChange={handleFilterChange}
                                    placeholder="Gan"
                                    InputProps={{ disableUnderline: true }}
                                    sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999' }}
                                />
                            </TableCell>

                            {/* ZP */}
                            <TableCell sx={{ bgcolor: '#e1f5fe' }}>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    name="zpName"
                                    value={filters.zpName}
                                    onChange={handleFilterChange}
                                    placeholder="ZP"
                                    InputProps={{ disableUnderline: true }}
                                    sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999' }}
                                />
                            </TableCell>

                            {/* STATUS */}
                            <TableCell sx={{ bgcolor: '#e1f5fe' }}>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    name="status"
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                    placeholder="Status"
                                    InputProps={{ disableUnderline: true }}
                                    sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999' }}
                                />
                            </TableCell>

                            {/* ACTIONS */}
                            <TableCell sx={{ bgcolor: '#e1f5fe' }} />
                        </TableRow>
                    </TableHead>

                    {/* BODY */}
                    <TableBody>
                        {filteredRecords.map((r) => (
                            <TableRow key={r.id} hover>
                                <TableCell>{r.id}</TableCell>
                                <TableCell>{r.name}</TableCell>
                                <TableCell>{formatDate(r.v_entry_date)}</TableCell>
                                <TableCell>{r.mobile}</TableCell>
                                <TableCell>{r.aadhar}</TableCell>
                                <TableCell>{r.epic_no}</TableCell>
                                <TableCell>{r.karad?.village || r.other?.village}</TableCell>
                                <TableCell>{r.karad?.ganName}</TableCell>
                                <TableCell>{r.karad?.zpName}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={r.status}
                                        size="small"
                                        sx={{
                                            bgcolor:
                                                r.status === 'Green'
                                                    ? '#4caf50'
                                                    : r.status === 'Yellow'
                                                        ? '#fbc02d'
                                                        : '#f44336',
                                            color: 'white',
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => onEdit(r)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteClick(r)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* DELETE CONFIRMATION DIALOG */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this record for{' '}
                        <strong>{recordToDelete?.name}</strong>? This action cannot be
                        undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}

export default React.memo(VisitorTable);
